/**
 * DI-2-3 — create the Data Acquisition for one position cluster.
 *
 *   under an open instruction  POST /inspection-instructions/{id}/data-acquisitions
 *   field-created (DI-D8)      POST /data-acquisitions  { assetId, siteId }
 *
 * The idempotency key is `externalId = uuidv5(INTAKE_NS, sessionId + ':' +
 * clusterId)`: the same session and cluster always produce the same key, so a
 * retry or a reload replays the SAME acquisition (`idempotentReplay: true`,
 * which is a success, not an error), and two clusters of one session never
 * share one.
 *
 * DI-3-C (inspection-3/v1 2.3.0, DI-3-B) — the standalone create may carry
 * `projectId` (the instruction is created under the pilot's OWN project: "none
 * open — will be created under <project>", edge b) and `assetRef` (the turbine
 * was deferred at Start: a placeholder park + turbine named with the pilot and
 * the time, at the cluster centroid — N1). Each is sent ONLY on that path;
 * `assetRef` is strict on the server since 2.3.0 (an unknown key → 400), so it
 * is exactly `{siteName, assetName, position: {lat, lon}}`. The create under
 * an instruction is unchanged.
 *
 * `workflow` (DI-D9 as extended 2026-09-25; DI-2-12): sent exactly when the
 * picker was shown — the field-created path, or an instruction whose project
 * defines none — and never when the project defines one (the server copies it,
 * and refuses a different value with 409 `workflow_defined_by_project`). A
 * create with no workflow to send and none on the project is refused with 422
 * `workflow_required`; the step never lets it leave (Continue is disabled).
 *
 * M3C-04 (inspection-3/v1 2.4.x) — the pick is sent as `workflowVersionId`
 * (an own version, or the version of an injection node SHARED with the
 * tenant). A shared node's set lands in the OWNER's tenant (DI-D58): the
 * create sends `X-Tenant-ID` = the owner's slug (`targetTenantSlug`, set by
 * `inspection3Fetch` — the one header place) and, once created, the set's
 * tenant is remembered so every later call of it sends the same slug.
 */
import {
  inspection3Fetch,
  isInspection3FetchError,
} from "../api/inspection3Fetch.js";
import { projectNotOpenWords } from "../api/projects.js";
import { deadlineWords, isRequestTimeout } from "../api/deadline.js";
import { rememberSetTenant } from "../api/setTenants.js";
import type {
  CreateDaUnderInstructionRequest,
  CreateStandaloneDaRequest,
  DataAcquisitionCreated,
} from "../api/types.js";

/**
 * The namespace of the intake's UUIDv5 keys. Fixed forever: changing it would
 * turn every in-flight session's retry into a second acquisition.
 */
export const INTAKE_NS = "6f1c2a9e-3b7d-4e25-9a41-0d8e5c7b2f16";

function uuidBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, "");
  if (!/^[0-9a-f]{32}$/i.test(hex)) throw new Error(`not a UUID: ${uuid}`);
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++)
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** RFC 9562 §5.5 — name-based UUID, SHA-1. */
export async function uuidV5(name: string, namespace: string): Promise<string> {
  const ns = uuidBytes(namespace);
  const nameBytes = new TextEncoder().encode(name);
  const buf = new Uint8Array(ns.length + nameBytes.length);
  buf.set(ns, 0);
  buf.set(nameBytes, ns.length);
  const hash = new Uint8Array(await crypto.subtle.digest("SHA-1", buf));
  const b = hash.slice(0, 16);
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x50;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function intakeExternalId(
  sessionId: string,
  clusterId: string,
): Promise<string> {
  return uuidV5(`${sessionId}:${clusterId}`, INTAKE_NS);
}

/** DI-3-C — the strict `AssetReference` the placeholder needs (2.3.0). */
export interface PlaceholderAssetRef {
  siteName: string;
  assetName: string;
  position: { lat: number; lon: number };
}

export interface CreateAcquisitionInput {
  sessionId: string;
  clusterId: string;
  /** `null` → the field-created (standalone) path. */
  instructionId: string | null;
  /** `null` on the standalone path when the turbine was deferred (DI-3-C). */
  assetId: string | null;
  siteId: string | null;
  /** DI-3-C — standalone only: the pilot's own project the instruction goes under. */
  projectId?: string | null;
  /** DI-3-C — standalone only, turbine deferred: the placeholder park + turbine. */
  assetRef?: PlaceholderAssetRef | null;
  /** The folder name — shown in the analyst list; ≤ 2 000 characters. */
  notes: string | null;
  /** The pilot's pick — sent only when the picker was shown (module header);
   *  `null` when the instruction's project defines the workflow. */
  workflow: string | null;
  /** M3C-04 — the version picked (own or shared), sent as `workflowVersionId`. */
  workflowVersionId?: string | null;
  /** M3C-04 — the OWNER's slug of a shared node's set; null/absent = own. */
  targetTenantSlug?: string | null;
  /** M3C-04 — the owner's name (remembered with the set). */
  targetTenantName?: string | null;
  /**
   * ZV-MIS-01C (2.8.0) — under an instruction only: the set's centroid when
   * the instruction is a mission opened to the drop's workflow (the server
   * re-checks; 409 `instruction_not_available` when it cannot take it).
   */
  dropPosition?: { lat: number; lon: number } | null;
}

/**
 * DI-2-12 (AC-6) — the workflow a create sends: the pilot's pick exactly when
 * the picker was shown, i.e. when nothing defines the workflow (field-created,
 * or `projectWorkflow: null`); NEVER when the project defines one — the server
 * copies it and would refuse a different value (409). No default: without a
 * pick this is `null`, and the step keeps Continue disabled.
 */
export function workflowToSend(
  projectWorkflow: string | null,
  pick: string | null,
): string | null {
  return projectWorkflow === null ? pick : null;
}

export async function createAcquisition(
  input: CreateAcquisitionInput,
): Promise<DataAcquisitionCreated> {
  const created = await sendCreate(input);
  if (input.targetTenantSlug)
    rememberSetTenant(created.dataAcquisitionId, {
      slug: input.targetTenantSlug,
      name: input.targetTenantName ?? null,
    });
  return created;
}

async function sendCreate(
  input: CreateAcquisitionInput,
): Promise<DataAcquisitionCreated> {
  const externalId = await intakeExternalId(input.sessionId, input.clusterId);
  const notes = input.notes ? input.notes.slice(0, 2000) : null;
  if (input.instructionId) {
    const body: CreateDaUnderInstructionRequest = {
      externalId,
      notes,
      ...(input.workflow ? { workflow: input.workflow } : {}),
      ...(input.workflowVersionId
        ? { workflowVersionId: input.workflowVersionId }
        : {}),
      ...(input.dropPosition
        ? {
            dropPosition: {
              lat: input.dropPosition.lat,
              lon: input.dropPosition.lon,
            },
          }
        : {}),
    };
    // an instruction is the caller's own — or (ZV-MIS-01C) a mission the
    // OWNER of a shared node opened to it: then the owner's slug, as the
    // standalone shared create sends it
    return inspection3Fetch<DataAcquisitionCreated>(
      `/inspection-instructions/${encodeURIComponent(input.instructionId)}/data-acquisitions`,
      { method: "POST", body, tenantSlug: input.targetTenantSlug ?? null },
    );
  }
  const body: CreateStandaloneDaRequest = {
    externalId,
    assetId: input.assetId,
    siteId: input.siteId,
    notes,
    // DI-3-C — only when the picker was shown: a project that defines the
    // workflow is the authority on this path too (2.3.0, 409 otherwise).
    ...(input.workflow ? { workflow: input.workflow } : {}),
    ...(input.workflowVersionId
      ? { workflowVersionId: input.workflowVersionId }
      : {}),
    ...(input.projectId ? { projectId: input.projectId } : {}),
    ...(input.assetRef
      ? {
          assetRef: {
            siteName: input.assetRef.siteName,
            assetName: input.assetRef.assetName,
            position: {
              lat: input.assetRef.position.lat,
              lon: input.assetRef.position.lon,
            },
          },
        }
      : {}),
  };
  return inspection3Fetch<DataAcquisitionCreated>("/data-acquisitions", {
    method: "POST",
    body,
    tenantSlug: input.targetTenantSlug ?? null,
  });
}

/**
 * DI-2-4 (inspection-3 2.1.0) — the acquisition an instruction already has,
 * when the create was refused with 409 `instruction_already_has_acquisition`
 * (II ↔ DA is 1:1, audit 1.15; it was a duplicate-key 500 until 2.1.0).
 * `null` for any other outcome.
 */
export function heldAcquisitionOf(error: unknown): string | null {
  if (!isInspection3FetchError(error) || error.status !== 409) return null;
  const body = (error.body ?? {}) as {
    reason?: unknown;
    dataAcquisitionId?: unknown;
  };
  return body.reason === "instruction_already_has_acquisition" &&
    typeof body.dataAcquisitionId === "string"
    ? body.dataAcquisitionId
    : null;
}

/** DI-2-12 — was the create refused because the project defines the workflow? */
export function projectDefinesWorkflow(error: unknown): boolean {
  if (!isInspection3FetchError(error) || error.status !== 409) return false;
  return (
    (error.body as { reason?: unknown } | null)?.reason ===
    "workflow_defined_by_project"
  );
}

/**
 * The create's refusal, in words (story §2.4). `underInstruction` matters for
 * one case: a second acquisition under one instruction violates II ↔ DA 1:1
 * (audit 1.15). From inspection-3 2.1.0 the gateway answers it with 409
 * `instruction_already_has_acquisition` and the existing id (DI-2-4), which is
 * named; any other failure on the instruction path still names it only as a
 * possibility, with the status.
 */
export function describeCreateError(
  error: unknown,
  underInstruction: boolean,
): string {
  // DI-3-S68 B1 — a timeout is a transport failure, worded as one
  if (isRequestTimeout(error))
    return `The data set could not be created — the server ${deadlineWords(error)}. Retry.`;
  if (!isInspection3FetchError(error)) {
    return "The data set could not be created — the server could not be reached. Try again.";
  }
  const body = (error.body ?? {}) as { reason?: string; state?: unknown };
  // M3C-10 (inspection-3 2.5.0) — the project is not in a state that accepts a drop
  if (error.status === 422 && body.reason === "project_not_open") {
    return projectNotOpenWords(body.state);
  }
  if (error.status === 422 && body.reason === "unknown_workflow") {
    return "The server does not know this workflow (unknown_workflow). Pick one of the listed workflows.";
  }
  if (error.status === 422 && body.reason === "workflow_not_granted") {
    return "Your company is not enabled for this workflow (workflow_not_granted). Pick another workflow or ask your administrator.";
  }
  if (error.status === 422 && body.reason === "workflow_required") {
    return "This data set needs a workflow and none is defined (workflow_required): the project defines none — choose one of the listed workflows.";
  }
  if (error.status === 409 && body.reason === "workflow_defined_by_project") {
    const project = (error.body as { projectWorkflow?: unknown })
      .projectWorkflow;
    const named = typeof project === "string" ? ` (${project})` : "";
    return `The instruction's project defines the workflow${named}, so it cannot be chosen here (workflow_defined_by_project). The instruction was reloaded — Continue takes the project's workflow.`;
  }
  // ZV-MIS-01C (2.8.0) — the mission was filled meanwhile / is no longer open here
  if (error.status === 409 && body.reason === "instruction_not_available") {
    return "This planned mission cannot take the drop any more — it was filled meanwhile or is no longer open here (instruction_not_available). Choose again below.";
  }
  const held = heldAcquisitionOf(error);
  if (held) {
    return `This instruction already has a data set (${held}). Add these images to it, pick another instruction, or continue field-created.`;
  }
  if (error.status === 409 && body.reason === "external_id_conflict") {
    return "This intake session already created a different data set under its id (external_id_conflict). Start the session over.";
  }
  if (underInstruction && (error.status === 409 || error.status >= 500)) {
    return `This instruction may already have a data set — pick another or continue field-created (HTTP ${String(error.status)}).`;
  }
  return `The data set could not be created (HTTP ${String(error.status)}).`;
}
